import React from 'react';

import styled from 'styled-components';

import { APIUserType } from '@cd3p/core/types/api';
import { Stack, SvgIcon, Typography } from 'components';

import { useNavigate, useSelector, useTranslation } from 'third-party';

import { apiDocsUrl, docsUrl } from 'constants/url';

import { useStorage } from 'modules/storage';

import { appSelectors } from 'selectors';

const Content = styled(Stack)`
  padding: 0.8rem;
  gap: 0.8rem;
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted100};
`;

const Title = styled(Typography)`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.bluegray500};
`;

const TopicItem = styled(Stack)`
  border: 1px solid ${props => props.theme.custom.palette.secondary200};
  padding: 0.8rem;
  cursor: pointer;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
`;

const TopicLabel = styled(Typography)`
  font-weight: 700;
  color: ${props => props.theme.custom.palette.secondary700};
`;

export const DIALOG_ITEM_HEIGHT = 85;
export const CONTRACTOR_DIALOG_ITEM_HEIGHT = 65;

type CommonHelpTopicsListProps = {
  onSelectCategory?: (categoryTitle: string) => void;
};

const UnreadBadge = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 1rem;
  background-color: ${props => props.theme.custom.palette.notificationBadge};
`;

export const CommonHelpTopicsList = ({
  onSelectCategory,
}: CommonHelpTopicsListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isApiDocsFeatureEnabled = useSelector(
    appSelectors.isApiDocsFeatureEnabled,
  );
  const userType = useSelector(appSelectors.userType);
  const unreadWhatsNews = useSelector(appSelectors.unreadWhatsNews);
  const { updateUserSettingInStorage } = useStorage();
  const showAPIDocs =
    userType !== APIUserType.Contractor && isApiDocsFeatureEnabled;

  const commonTopics = [
    {
      icon: 'help-center-rocket',
      title: t('helpCenter.commonTopic.gettingStarted'),
      unread: false,
      get onClick() {
        return () => onSelectCategory?.(this.title);
      },
    },
    {
      icon: 'help-center-what-is-new',
      title: t('helpCenter.commonTopic.whatIsNew'),
      unread: unreadWhatsNews,
      get onClick() {
        return () => {
          updateUserSettingInStorage({
            lastWhatsNewAppVersion: APPLICATION_VERSION,
          });

          navigate(docsUrl());
        };
      },
    },
    ...(showAPIDocs
      ? [
          {
            icon: 'help-center-api',
            title: t('helpCenter.commonTopic.apiDocs'),
            unread: false,
            get onClick() {
              return () => navigate(apiDocsUrl());
            },
          },
        ]
      : []),
  ];
  return (
    <Content>
      <Title variant="body2">{t('helpCenter.panel.commonTopicsTitle')}</Title>
      {commonTopics.map(topic => (
        <TopicItem key={topic.icon} onClick={topic.onClick}>
          <SvgIcon height="2rem" icon={topic.icon} width="2rem" />
          <TopicLabel variant="body2">{topic.title}</TopicLabel>
          {topic.unread && <UnreadBadge />}
        </TopicItem>
      ))}
    </Content>
  );
};
